"use client";
import * as React from "react";
import ReactDatePicker from "react-datepicker";
import { offset } from "@floating-ui/react";
import { tv } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps, forwardRef } from "react";
import { CalendarDollarIcon } from "@repo/ui-parkmobile/icons/CalendarDollarIcon";
import { ErrorIcon } from "@repo/ui-parkmobile/icons/ErrorIcon";
import { ClockIcon } from "@repo/ui-parkmobile/icons/ClockIcon";
import { Dropdown, DropdownTrigger } from "./Dropdown";
import { input as inputStyles } from "./Input";

import "react-datepicker/dist/react-datepicker.css";
import "./DatePicker.css";

const datePickerContainer = tv({
  base: "uipm:relative uipm:w-full uipm:md:min-w-[250px]",
});

const datePickerWrapper = tv({
  base: cn(
    "date-picker-wrapper",
    "[&_.react-datepicker-wrapper]:uipm:w-full",
    "[&_.react-datepicker__input-container]:uipm:w-full",
    "[&_.react-datepicker__input-container>input]:uipm:w-full",
  ),
});

type ReadOnlyInputProps = {
  value?: string;
  onClick?: () => void;
  id?: string;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  state?: "default" | "hover" | "error" | "disabled";
  isHovered?: boolean;
  background?: "default" | "white";
  startDate?: Date | null;
  endDate?: Date | null;
  selectsRange?: boolean;
};

const ReadOnlyInput = forwardRef<HTMLInputElement, ReadOnlyInputProps>(
  (
    {
      value,
      onClick,
      id,
      name,
      placeholder,
      disabled,
      state,
      isHovered,
      background = "default",
      startDate,
      endDate,
      selectsRange,
    },
    ref,
  ) => {
    const inputState = disabled ? "disabled" : state === "error" ? "error" : "default";

    const displayValue = startDate
      ? formatDateRange(startDate, endDate || null)
      : value;

    return (
      <input
        readOnly
        className={cn(
          inputStyles({ state: inputState, background }),
          "uipm:pr-12 uipm:cursor-pointer",
          state === "error" && "uipm:pr-20",
        )}
        onClick={onClick}
        ref={ref}
        value={displayValue}
        id={id}
        name={name}
        placeholder={placeholder}
        disabled={disabled}
      />
    );
  },
);

ReadOnlyInput.displayName = "ReadOnlyInput";

function formatDateRange(start: Date | null, end: Date | null): string {
  if (!start) return "";

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const formatDate = (date: Date) => {
    const day = date.getDate();
    const month = monthNames[date.getMonth()];
    return `${day} ${month}`;
  };

  if (end && start.getTime() !== end.getTime()) {
    const startStr = formatDate(start);
    const endStr = formatDate(end);
    const year = end.getFullYear();
    return `${startStr} - ${endStr}, ${year}`;
  } else {
    const day = start.getDate();
    const month = monthNames[start.getMonth()];
    const year = start.getFullYear();
    return `${day} ${month}, ${year}`;
  }
}

function formatTimeFromDate(date: Date | null): string | null {
  if (!date) return null;
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
}

function stripTime(date: Date | null): Date | null {
  if (!date) return null;
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
}

export type DatePickerValue = {
  startDate: Date | null;
  endDate: Date | null;
  startTime: string | null;
  endTime: string | null;
};

export type DatePickerProps = Omit<
  ComponentProps<"input">,
  "onChange" | "value" | "defaultValue"
> & {
  containerClassName?: string;
  popperClassName?: string;
  state?: "default" | "error" | "disabled";
  background?: "default" | "white";
  defaultValue?: DatePickerValue;
  onChange?: (value: DatePickerValue) => void;
  dateFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  placeholderText?: string;
  showTimeSelect?: boolean;
  timeFormat?: string;
  timeIntervals?: number;
  selectsRange?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function DatePicker({
  containerClassName,
  popperClassName,
  state,
  background = "default",
  disabled,
  defaultValue,
  onChange,
  dateFormat = "MM/dd/yyyy",
  minDate,
  maxDate,
  placeholderText = "Select date",
  showTimeSelect = false,
  timeFormat = "HH:mm",
  timeIntervals = 15,
  selectsRange = false,
  open: controlledOpen,
  onOpenChange,
  id,
  name,
  className,
}: DatePickerProps) {
  const [isHovered, setIsHovered] = React.useState(false);
  const [internalOpen, setInternalOpen] = React.useState(false);

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const setIsOpen = (open: boolean) => {
    if (!isControlled) {
      setInternalOpen(open);
    }
    onOpenChange?.(open);
  };
  const [internalStartDate, setInternalStartDate] = React.useState<Date | null>(
    defaultValue?.startDate || null,
  );
  const [internalEndDate, setInternalEndDate] = React.useState<Date | null>(
    defaultValue?.endDate || null,
  );
  const [startTime, setStartTime] = React.useState<string>(
    defaultValue?.startTime || "12:00",
  );
  const [endTime, setEndTime] = React.useState<string>(
    defaultValue?.endTime || "13:00",
  );

  const notifyChange = (
    start: Date | null,
    end: Date | null,
    startTimeStr: string,
    endTimeStr: string,
  ) => {
    onChange?.({
      startDate: stripTime(start),
      endDate: stripTime(end),
      startTime: showTimeSelect && startTimeStr ? startTimeStr : null,
      endTime: showTimeSelect && endTimeStr ? endTimeStr : null,
    });
  };

  const handleChange = (date: Date | [Date | null, Date | null] | null) => {
    if (selectsRange && Array.isArray(date)) {
      const [start, end] = date;

      if (start && end && start.getTime() === end.getTime()) {
        setInternalStartDate(start);
        setInternalEndDate(end);
        notifyChange(start, end, startTime, endTime);
        setIsOpen(false);
        return;
      }

      setInternalStartDate(start);
      setInternalEndDate(end);
      notifyChange(start, end || start, startTime, endTime);

      if (end) {
        setIsOpen(false);
      }
    } else if (!selectsRange && date) {
      const singleDate = date as Date;
      setInternalStartDate(singleDate);
      notifyChange(singleDate, null, startTime, "");
      setIsOpen(false);
    } else {
      setInternalStartDate(null);
      notifyChange(null, null, "", "");
    }
  };

  const timeOptions = React.useMemo(() => {
    return Array.from({ length: 24 }, (_, i) => {
      const hour24 = i;
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const period = hour24 >= 12 ? "PM" : "AM";
      const displayHour = hour12.toString().padStart(2, "0");
      const value = `${hour24.toString().padStart(2, "0")}:00`;

      return {
        label: `${displayHour}:00 ${period}`,
        value: value,
      };
    });
  }, []);

  const formatTime12Hour = (time24: string) => {
    if (!time24) return "";
    const parts = time24.split(":");
    if (!parts[0]) return "";
    const hour24 = parseInt(parts[0]);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const period = hour24 >= 12 ? "PM" : "AM";
    return `${hour12.toString().padStart(2, "0")}:00 ${period}`;
  };

  const CalendarContainer = ({
    className,
    children,
  }: {
    className?: string;
    children: React.ReactNode;
  }) => (
    <div
      className={className}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--Margin-margin-xs, 8px)",
      }}
    >
      <div>{children}</div>
      {showTimeSelect && (
        <div className="uipm:px-4 uipm:pb-4 uipm:bg-white uipm:w-full uipm:rounded-b-xl">
          <div className="uipm:relative uipm:flex uipm:gap-3 uipm:w-full">
            <div className="uipm:flex-1">
              <Dropdown
                trigger={
                  <DropdownTrigger
                    background="default"
                    placeholder="from"
                    selectedLabel={
                      startTime ? formatTime12Hour(startTime) : undefined
                    }
                    className="uipm:min-h-[56px] uipm:cursor-pointer"
                    showChevron={false}
                  >
                    <div className="uipm:flex uipm:items-center uipm:justify-between uipm:w-full">
                      <span className="uipm:flex-1 uipm:text-left uipm:text-[var(--typography-colors-text-faded-dark,#575757)] uipm:font-['Inter'] uipm:font-medium uipm:text-[length:var(--typography-sizes-body-medium)] uipm:leading-[var(--typography-line-heights-body-medium)]">
                        {startTime ? formatTime12Hour(startTime) : "From"}
                      </span>
                      {!startTime && (
                        <ClockIcon
                          size={20}
                          color="var(--typography-colors-text-accent)"
                        />
                      )}
                    </div>
                  </DropdownTrigger>
                }
                options={timeOptions}
                value={startTime}
                onValueChange={(value) => {
                  setStartTime(value);
                  notifyChange(
                    internalStartDate,
                    internalEndDate,
                    value,
                    endTime,
                  );

                  if (selectsRange) {
                    if (
                      internalStartDate &&
                      internalEndDate &&
                      value &&
                      endTime
                    ) {
                      setIsOpen(false);
                    }
                  } else {
                    if (internalStartDate && value) {
                      setIsOpen(false);
                    }
                  }
                }}
                showNoneOption={false}
                className="uipm:static"
                align="center"
                contentClassName="uipm:mt-[var(--spacing-primitives-12)] uipm:border-1 uipm:w-[300px]"
              />
            </div>
            {selectsRange && (
              <div className="uipm:flex-1">
                <Dropdown
                  className="uipm:static"
                  align="center"
                  trigger={
                    <DropdownTrigger
                      background="default"
                      placeholder="until"
                      selectedLabel={
                        endTime ? formatTime12Hour(endTime) : undefined
                      }
                      className="uipm:min-h-[56px] uipm:cursor-pointer"
                      showChevron={false}
                    >
                      <div className="uipm:flex uipm:items-center uipm:justify-between uipm:w-full">
                        <span className="uipm:flex-1 uipm:text-left uipm:text-[var(--typography-colors-text-faded-dark,#575757)] uipm:font-['Inter'] uipm:font-medium uipm:text-[length:var(--typography-sizes-body-medium)] uipm:leading-[var(--typography-line-heights-body-medium)]">
                          {endTime ? formatTime12Hour(endTime) : "Until"}
                        </span>
                        {!endTime && (
                          <ClockIcon
                            size={20}
                            color="var(--typography-colors-text-accent)"
                          />
                        )}
                      </div>
                    </DropdownTrigger>
                  }
                  options={timeOptions}
                  value={endTime}
                  onValueChange={(value) => {
                    setEndTime(value);
                    notifyChange(
                      internalStartDate,
                      internalEndDate,
                      startTime,
                      value,
                    );

                    if (
                      internalStartDate &&
                      internalEndDate &&
                      startTime &&
                      value
                    ) {
                      setIsOpen(false);
                    }
                  }}
                  showNoneOption={false}
                  contentClassName="uipm:mt-[var(--spacing-primitives-12)] uipm:border-1 uipm:w-[300px]"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div
      className={cn(
        datePickerContainer(),
        datePickerWrapper(),
        containerClassName,
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {selectsRange ? (
        <ReactDatePicker
          selected={internalStartDate}
          startDate={internalStartDate}
          endDate={internalEndDate}
          selectsRange={true}
          onChange={handleChange as any}
          dateFormat={dateFormat}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          placeholderText={placeholderText}
          wrapperClassName="uipm:w-full"
          popperClassName={cn("uipm:z-[200]", popperClassName)}
          calendarContainer={CalendarContainer}
          open={isOpen}
          onClickOutside={() => {
            if (internalStartDate && !internalEndDate) {
              setInternalEndDate(internalStartDate);
            }
            setIsOpen(false);
          }}
          onInputClick={() => setIsOpen(true)}
          popperPlacement="bottom"
          popperModifiers={[offset(12)]}
          customInput={
            <ReadOnlyInput
              id={id}
              name={name}
              state={state}
              isHovered={isHovered}
              disabled={disabled}
              placeholder={placeholderText}
              background={background}
              startDate={internalStartDate}
              endDate={internalEndDate}
              selectsRange={true}
            />
          }
        />
      ) : (
        <ReactDatePicker
          selected={internalStartDate}
          onChange={handleChange as any}
          dateFormat={dateFormat}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          placeholderText={placeholderText}
          wrapperClassName="uipm:w-full"
          popperClassName={cn("uipm:z-[9999]", popperClassName)}
          calendarContainer={CalendarContainer}
          open={isOpen}
          onClickOutside={() => setIsOpen(false)}
          onInputClick={() => setIsOpen(true)}
          popperPlacement="bottom"
          popperModifiers={[offset(12)]}
          customInput={
            <ReadOnlyInput
              id={id}
              name={name}
              state={state}
              isHovered={isHovered}
              disabled={disabled}
              placeholder={placeholderText}
              background={background}
              startDate={internalStartDate}
              endDate={null}
              selectsRange={false}
            />
          }
        />
      )}
      <div className="uipm:absolute uipm:right-4 uipm:top-1/2 uipm:-translate-y-1/2 uipm:pointer-events-none uipm:flex uipm:items-center uipm:gap-2">
        {state === "error" && <ErrorIcon />}
        <CalendarDollarIcon size={24} />
      </div>
    </div>
  );
}
